import { computed } from "@preact/signals-react";
import classNames from "classnames";
import {
  DotsMenuBarCore,
  DropDownBehaviour,
  IMenuItem,
  IMenuItemState,
  MenuBarOrientation,
  MenuBarUtils,
  MenuItemType,
  MenuState,
} from "dots-ui/dotsMenuBar";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useMatches } from "react-router-dom";
import { isMobileView } from "../../appStates/appStates";

interface IAvisNavMenuBarProps {
  children?: React.JSX.Element | React.JSX.Element[];
  className?: string;
  horizontalMenuItems: MenuItemType[];
  verticalMenuItems: IMenuItem[];
}

function KeyToMenuState(itemKey?: string): MenuState {
  if (!itemKey?.length) {
    return {};
  }

  const result: MenuState = {};

  result[itemKey] = {
    isActive: true,
  };

  return result;
}

export function AvisNavMenuBar(props: Readonly<IAvisNavMenuBarProps>) {
  const { horizontalMenuItems, verticalMenuItems } = props;

  const matches = useMatches();
  const location = useLocation();

  const [itemStateHorizontal, setItemStateHorizontal] = useState<{
    [id: string]: IMenuItemState;
  }>({});

  const [itemStateVertical, setItemStateVertical] = useState<{
    [id: string]: IMenuItemState;
  }>({});

  useEffect(() => {
    // set active item on location change
    setItemStateVertical(KeyToMenuState(matches[matches.length - 1].id));
  }, [location, matches]);

  const dropDownBehaviour = (orientation?: MenuBarOrientation) => {
    return computed((): DropDownBehaviour => {
      if (isMobileView.value) {
        return "outside-full-width";
      }

      if (orientation == "vertical") {
        return "inside";
      }

      return "outside";
    });
  };

  const itemsFlatMapHorizontal = useMemo(() => {
    const menuItems = [
      ...(horizontalMenuItems.filter(
        (x) => typeof x !== "string",
      ) as IMenuItem[]),
    ];

    return MenuBarUtils.FlatMapMenuItemParents(menuItems);
  }, [horizontalMenuItems]);

  const itemsFlatMapVertical = useMemo(() => {
    const menuItems = [
      ...verticalMenuItems.filter((x) => typeof x !== "string"),
    ];

    return MenuBarUtils.FlatMapMenuItemParents(menuItems);
  }, [verticalMenuItems]);

  const onItemClickCallback = useCallback(
    (
      e: { originalEvent: React.MouseEvent; item: IMenuItem },
      orientation: MenuBarOrientation,
    ) => {
      if (!e.item.key) return;

      const isHorizontal = orientation === "horizontal";

      if (
        isHorizontal &&
        isMobileView.value &&
        e.item.key === "mobile-toggle" &&
        (!itemStateHorizontal ||
          Object.keys(itemStateHorizontal).length === 0 ||
          itemStateHorizontal["mobile-toggle"]?.isActive === false)
      ) {
        const activeItem = e.item.items?.find((x) =>
          matches.some((m) => m.id === x.key),
        );

        if (activeItem) {
          const toggleSubitemsState =
            MenuBarUtils.OnItemClickSelectSingleOption(
              activeItem,
              itemsFlatMapHorizontal,
              itemStateHorizontal,
              "keep-open",
            );

          setItemStateHorizontal(toggleSubitemsState);

          return;
        }
      }

      const newItemState = MenuBarUtils.OnItemClickSelectSingleOption(
        e.item,
        isHorizontal ? itemsFlatMapHorizontal : itemsFlatMapVertical,
        isHorizontal ? itemStateHorizontal : itemStateVertical,
        isHorizontal ? "close" : "keep-open",
      );

      isHorizontal
        ? setItemStateHorizontal(newItemState)
        : setItemStateVertical(newItemState);
    },
    [
      itemsFlatMapHorizontal,
      itemsFlatMapVertical,
      itemStateHorizontal,
      matches,
      itemStateVertical,
    ],
  );

  const onMenuBarOutsideClick = () => {
    const newItemState = cloneDeep(itemStateHorizontal);

    MenuBarUtils.ClearActiveState(newItemState);

    setItemStateHorizontal(newItemState);
  };

  const className = classNames("avis-nav-menu", props.className);
  const verticalClassName = classNames("avis-nav-vertical", className);
  const horizontalClassName = classNames(
    "avis-nav-horizontal",
    "dots-topbar",
    className,
  );

  return (
    <>
      <DotsMenuBarCore
        id="dots-nav-horizontal"
        className={horizontalClassName}
        items={horizontalMenuItems}
        orientation="horizontal"
        dropDownBehaviour={dropDownBehaviour().value}
        itemState={itemStateHorizontal}
        onItemClick={(e) => onItemClickCallback(e, "horizontal")}
        onMenubarSubOutsideClick={onMenuBarOutsideClick}
      />
      <div className="vertical-nav-wrapper">
        {!isMobileView.value && verticalMenuItems.length > 0 && (
          <DotsMenuBarCore
            id="dots-nav-vertical"
            className={verticalClassName}
            items={verticalMenuItems}
            orientation="vertical"
            dropDownBehaviour={dropDownBehaviour("vertical").value}
            itemState={itemStateVertical}
            onItemClick={(e) => onItemClickCallback(e, "vertical")}
            isScrollable
          />
        )}
        {props.children}
      </div>
    </>
  );
}
